import { produce } from 'immer';

import React, { useState } from 'react';
import { createStore, StateCreator, StoreApi, useStore } from 'zustand';

export enum NestedAccordionsValues {
  None = 'none',
  ExpandAll = 'expand-all',
  CollapseAll = 'collapse-all',
}

type NestedAccordionsValue = NestedAccordionsValues;

type NestedAccordionsState = {
  globalValue: NestedAccordionsValue;
  onChangeGlobalValue: (action: NestedAccordionsValue) => void;
};

type State = Record<string, any>;

export function createContextStore<T extends State>(initialState: StateCreator<T>) {
  const Context = React.createContext<StoreApi<T> | undefined>(undefined);

  /**
   * Provider component that wraps the app and provides the store. It creates a new store instance.
   */
  const Provider = ({ children }: { children: React.ReactNode }) => {
    const [store] = useState(() => createStore(initialState));

    return <Context.Provider value={store}>{children}</Context.Provider>;
  };

  /**
   * Hook to access the store state using a selector.
   */
  function useContextStore<U>(selector: (store: T) => U) {
    const store = React.useContext(Context);
    if (!store) {
      throw new Error('useContextStore must be used within a Provider');
    }

    return useStore(store, selector);
  }

  /**
   * Hook to access the store API directly.
   */
  function useImperativeApi() {
    const store = React.useContext(Context);

    if (!store) {
      throw new Error('useImperativeApi must be used within a Provider');
    }

    return store;
  }

  return { Provider, useContextStore, useImperativeApi };
}

const {
  Provider: NestedAccordionsStoreProvider,
  useContextStore: useNestedAccordionsStore,
  useImperativeApi: useNestedAccordionsApi,
} = createContextStore<NestedAccordionsState>(set => ({
  globalValue: NestedAccordionsValues.None,
  onChangeGlobalValue: globalValue => {
    set(
      produce((state: NestedAccordionsState) => {
        state.globalValue = globalValue;
      }),
    );
  },
}));

type NestedAccordionsApi = ReturnType<typeof useNestedAccordionsApi>;

export { NestedAccordionsStoreProvider, useNestedAccordionsStore, useNestedAccordionsApi };
export type { NestedAccordionsApi };
